import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { currencySymbol, date, money, toDecimal } from '../components/format';
import { SubscriptionBadge } from '../components/billing';
import { BillingProfileCard } from '../components/billingProfile';
import { Flag } from '../components/Flag';
import { Alert, Badge, Button, Card, Field, Select, Spinner } from '../components/ui';
import { ApiError, api } from '../services/api';
import { useSession } from '../session';
import type { Claim, ClaimKind, Invitation, Member, MemberCapChoice, Organization } from '../types';

export function OrganizationPage() {
  const { t } = useTranslation();
  const { user, reload } = useSession();
  const location = useLocation();
  const joined = (location.state as { joined?: string } | null)?.joined;
  const [org, setOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [fullPool, setFullPool] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const o = await api.organization();
      setOrg(o);
      const admin = o.role === 'owner' || o.role === 'admin';
      const [m, i, c] = await Promise.all([api.members(), admin ? api.invitations() : Promise.resolve([]), admin ? api.claims() : Promise.resolve([])]);
      setMembers(m);
      setInvitations(i);
      setClaims(c);
      // The period's whole pool explains the suggested default limit; the suggestion stands without it.
      if (admin && o.suggested_member_cap_eur !== null)
        api.usage().then((u) => setFullPool(u.budget_full), () => setFullPool(null));
    } catch {
      setError(t('app.error'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // The billing page links to /organization#billing-profile: scroll there once the page is drawn.
  const loaded = org !== null;
  useEffect(() => {
    if (loaded && location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView?.({ block: 'start' });
  }, [loaded, location.hash]);

  if (error) return <Alert kind="error">{error}</Alert>;
  if (!org || !user) return <Spinner />;
  const admin = org.role === 'owner' || org.role === 'admin';

  return (
    <div className="flex flex-col gap-4">
      {joined && <Alert kind="success">{t('invite.accepted', { organization: joined })}</Alert>}
      {org.kind === 'personal' && <Alert kind="info">{t('org.personalHint')}</Alert>}
      <DetailsCard org={org} admin={admin} onSaved={setOrg} />
      <BillingProfileCard key={org.id} org={org} admin={admin} onSaved={setOrg} />
      <ModelsCard org={org} admin={admin} onSaved={setOrg} />
      <ReviewSampleCard org={org} onSaved={setOrg} />
      {admin && <DefaultCapCard org={org} fullPool={fullPool} onChanged={load} />}
      <MembersCard org={org} members={members} me={user.id} onChanged={load} />
      {admin && <InvitationsCard org={org} invitations={invitations} onChanged={load} />}
      {admin && <ClaimsCard claims={claims} onChanged={load} />}
      <LeaveCard org={org} onLeft={reload} />
    </div>
  );
}

function DetailsCard({ org, admin, onSaved }: { org: Organization; admin: boolean; onSaved: (o: Organization) => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: org.name, billing_email: org.billing_email ?? '' });
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState('saving');
    try {
      onSaved(await api.patchOrganization({ name: form.name.trim(), billing_email: form.billing_email.trim() || null }));
      setState('saved');
    } catch {
      setState('error');
    }
  };
  return (
    <Card
      title={t('org.title')}
      actions={
        <Link to="/billing" title={t('org.seeBilling')}>
          <SubscriptionBadge sub={org.subscription} />
        </Link>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field id="org-name" label={t('org.name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!admin} required />
        <Field id="billing-email" label={t('org.billingEmail')} hint={t('org.billingEmailHint')} type="email" value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} disabled={!admin} />
        {!admin && <p className="text-[12px] text-faint">{t('org.readOnly')}</p>}
        {state === 'saved' && <Alert kind="success">{t('app.saved')}</Alert>}
        {state === 'error' && <Alert kind="error">{t('app.error')}</Alert>}
        {admin && (
          <div>
            <Button type="submit" disabled={state === 'saving' || !form.name.trim()}>
              {state === 'saving' ? t('app.saving') : t('app.save')}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

/** The review's sample (sculpt-api data-policy review DEC-08): the owner of an organization on a paid offer keeps its
 *  conversations out of it; those where Sculpt failed, or that members report or rate down, are still reviewed. Shown to
 *  the owner who may change it, and to every member once it is set. The checkbox saves at once. */
function ReviewSampleCard({ org, onSaved }: { org: Organization; onSaved: (o: Organization) => void }) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const since = org.review_sample_opt_out_at;
  if (!org.review_sample_opt_out_available && since === null) return null;
  const toggle = async (optedOut: boolean) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await api.setReviewSample(optedOut));
    } catch (err) {
      setError(err instanceof ApiError && err.is('free_offer') ? t('reviewSample.freeOffer') : t('app.error'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title={t('reviewSample.title')}>
      <p className="text-[13px] text-muted">{t('reviewSample.intro')}</p>
      {org.role === 'owner' ? (
        <label className="mt-4 flex items-center gap-2 text-[13px] text-ink">
          <input
            type="checkbox"
            className="rounded-sm border-line text-accent focus:ring-0 disabled:opacity-60"
            checked={since !== null}
            disabled={busy}
            onChange={(e) => void toggle(e.target.checked)}
          />
          <span>{t('reviewSample.optOut')}</span>
        </label>
      ) : (
        <p className="mt-1 text-[12px] text-faint">{t('reviewSample.ownerOnly')}</p>
      )}
      {since !== null && <p className="mt-2 text-[12px] text-muted">{t('reviewSample.since', { date: date(since, i18n.language) })}</p>}
      {error && (
        <div className="mt-3">
          <Alert kind="error">{error}</Alert>
        </div>
      )}
    </Card>
  );
}

/** The models the organization's members may use (sculpt-api PLAN-model-choice.md): owners and admins refuse some,
 *  and a refused model is never called for the organization. Each toggle saves at once; the last allowed model stays. */
function ModelsCard({ org, admin, onSaved }: { org: Organization; admin: boolean; onSaved: (o: Organization) => void }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allowedCount = org.models.filter((m) => m.allowed).length;
  // Cheap or dear drawn in the organization's currency: €, €€ or $, $$.
  const sign = currencySymbol(org.currency, 'en');
  const toggle = async (id: string, allowed: boolean) => {
    setBusy(true);
    setError(null);
    const refused = org.models.filter((m) => (m.id === id ? !allowed : !m.allowed)).map((m) => m.id);
    try {
      onSaved(await api.setRefusedModels(refused));
    } catch (err) {
      setError(err instanceof ApiError && err.is('no_model_left') ? t('models.noModelLeft') : t('app.error'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title={t('models.title')}>
      <p className="text-[13px] text-muted">{t('models.intro')}</p>
      {!admin && <p className="mt-1 text-[12px] text-faint">{t('models.readOnly')}</p>}
      <ul className="mt-4 flex flex-col divide-y divide-line-row">
        {org.models.map((m) => {
          const last = m.allowed && allowedCount <= 1;
          return (
            <li key={m.id} className="flex flex-col gap-1 py-2 text-[13px]" data-testid={`model-${m.id}`}>
              <label className="flex items-center gap-2 text-ink">
                <input
                  type="checkbox"
                  className="rounded-sm border-line text-accent focus:ring-0 disabled:opacity-60"
                  checked={m.allowed}
                  disabled={!admin || busy || last}
                  onChange={(e) => void toggle(m.id, e.target.checked)}
                />
                <span className="font-medium">{m.label}</span>
                {m.price_tier && (
                  <span title={t(`models.price${m.price_tier}`)} aria-label={t(`models.price${m.price_tier}`)} className="font-mono text-[12px] tracking-[-0.5px]">
                    <span className="text-muted">{sign.repeat(m.price_tier)}</span>
                    <span className="text-line">{sign.repeat(Math.max(0, 2 - m.price_tier))}</span>
                  </span>
                )}
              </label>
              <div className="ml-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted">
                {m.maker !== m.host && <span>{t('models.madeBy', { maker: m.maker })}</span>}
                <span className="inline-flex items-center gap-1.5">
                  {t('models.hostedBy', { host: m.host })}
                  <Flag code={m.host_country} />
                </span>
                <span className="inline-flex items-center gap-1.5">
                  {t('models.dataIn')}
                  {m.data_location.map((c) => (
                    <Flag key={c} code={c} />
                  ))}
                </span>
              </div>
              {admin && last && <p className="ml-6 text-[11.5px] text-faint">{t('models.lastAllowed')}</p>}
            </li>
          );
        })}
      </ul>
      {error && (
        <div className="mt-3">
          <Alert kind="error">{error}</Alert>
        </div>
      )}
    </Card>
  );
}

function MembersCard({ org, members, me, onChanged }: { org: Organization; members: Member[]; me: number; onChanged: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const admin = org.role === 'owner' || org.role === 'admin';
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError && err.is('not_allowed') ? t('app.error') : t('app.error'));
    }
  };
  return (
    <Card title={t('org.members')} actions={<span className="text-[12px] text-muted">{t('org.membersCount', { count: members.length })}</span>}>
      {error && <Alert kind="error">{error}</Alert>}
      <div className="-mx-2 overflow-x-auto">
        <table className="w-full min-w-[520px] text-[13px]">
          <thead>
            <tr className="text-left font-mono text-[10.5px] uppercase tracking-wider text-faint">
              <th className="px-2 py-1.5 font-normal">{t('auth.email')}</th>
              <th className="px-2 py-1.5 font-normal">{t('org.inviteRole')}</th>
              <th className="px-2 py-1.5 font-normal">{t('org.joined')}</th>
              {admin && <th className="px-2 py-1.5 font-normal">{t(org.subscription?.trial ? 'org.capTrial' : 'org.cap')}</th>}
              {admin && <th className="px-2 py-1.5" />}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.user_id} className="border-t border-line-row" data-testid={`member-${m.user_id}`}>
                <td className="px-2 py-2">
                  <div className="text-ink">
                    {`${m.first_name} ${m.last_name}`.trim() || m.email}
                    {m.user_id === me && <span className="ml-1.5 font-mono text-[10.5px] text-faint">({t('org.you')})</span>}
                  </div>
                  <div className="font-mono text-[11px] text-faint">{m.email}</div>
                </td>
                <td className="px-2 py-2">
                  <Badge tone={m.role === 'owner' ? 'accent' : 'neutral'}>{t(`role.${m.role}`)}</Badge>
                  {!m.email_verified && <Badge tone="warn">{t('org.unverified')}</Badge>}
                  {!m.is_active && <Badge tone="danger">{t('org.inactive')}</Badge>}
                </td>
                <td className="px-2 py-2 text-muted">{date(m.joined_at, i18n.language)}</td>
                {admin && (
                  <td className="px-2 py-2">
                    <CapEditor member={m} defaultCap={org.default_member_cap_eur} currency={org.currency} onSaved={onChanged} />
                  </td>
                )}
                {admin && (
                  <td className="px-2 py-2 text-right">
                    {m.user_id !== me && m.role !== 'owner' && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" className="px-2! py-1! text-[12px]" onClick={() => void run(() => api.setMemberRole(m.user_id, m.role === 'admin' ? 'member' : 'admin'))}>
                          {m.role === 'admin' ? t('org.makeMember') : t('org.makeAdmin')}
                        </Button>
                        {org.role === 'owner' && (
                          <Button
                            variant="ghost"
                            className="px-2! py-1! text-[12px]"
                            onClick={() => {
                              if (window.confirm(t('org.transferConfirm', { email: m.email }))) void run(() => api.transferOwnership(m.user_id));
                            }}
                          >
                            {t('org.transfer')}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          className="px-2! py-1! text-[12px] text-danger!"
                          onClick={() => {
                            if (window.confirm(t('org.removeConfirm', { email: m.email }))) void run(() => api.removeMember(m.user_id));
                          }}
                        >
                          {t('org.remove')}
                        </Button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** The limit per period every member without their own inherits, pre-filled with the period's credits shared by the
 *  seats. Members keep their own limit, or no limit when an admin exempted them. */
function DefaultCapCard({ org, fullPool, onChanged }: { org: Organization; fullPool: number | null; onChanged: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const trial = org.subscription?.trial ?? false;
  // Caps are in the organization's currency (the `_eur` names are historical).
  const cur = org.currency;
  const current = org.default_member_cap_eur;
  const suggested = org.suggested_member_cap_eur;
  const [value, setValue] = useState(current ?? suggested ?? '');
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'invalid' | 'error'>('idle');
  const save = async (cap: string | null) => {
    setState('saving');
    try {
      await api.setDefaultMemberCap(cap);
      if (cap === null) setValue(suggested ?? '');
      await onChanged();
      setState('saved');
    } catch {
      setState('error');
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const cap = toDecimal(value);
    if (cap === null) return setState('invalid');
    void save(cap);
  };
  const seats = org.subscription?.seats ?? 0;
  return (
    <Card title={t(trial ? 'org.defaultCapTrial' : 'org.defaultCap')}>
      <p className="text-[13px] text-muted" data-testid="default-cap-current">
        {current === null ? t('org.defaultCapNone') : t(trial ? 'org.defaultCapCurrentTrial' : 'org.defaultCapCurrent', { amount: money(current, cur, l) })}
      </p>
      <p className="mt-1 text-[12px] text-faint">{t('org.defaultCapHint')}</p>
      <form onSubmit={submit} className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end" data-testid="default-cap-form">
        <div className="sm:w-48">
          <Field id="default-cap" label={t('org.defaultCapLabel', { symbol: currencySymbol(cur, l) })} inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} required />
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={state === 'saving' || !value.trim()}>
            {state === 'saving' ? t('app.saving') : t('app.save')}
          </Button>
          {current !== null && (
            <Button variant="ghost" onClick={() => void save(null)} disabled={state === 'saving'}>
              {t('org.defaultCapRemove')}
            </Button>
          )}
        </div>
      </form>
      {suggested !== null && (
        <p className="mt-2 text-[12px] text-muted" data-testid="default-cap-suggested">
          {fullPool !== null && seats > 0
            ? t(trial ? 'org.defaultCapSuggestedTrial' : 'org.defaultCapSuggested', { amount: money(suggested, cur, l), pool: money(fullPool, cur, l), count: seats })
            : t('org.defaultCapSuggestedPlain', { amount: money(suggested, cur, l) })}
        </p>
      )}
      {state === 'saved' && (
        <div className="mt-3">
          <Alert kind="success">{t('app.saved')}</Alert>
        </div>
      )}
      {(state === 'error' || state === 'invalid') && (
        <div className="mt-3">
          <Alert kind="error">{state === 'invalid' ? t('org.capInvalid', { currency: cur }) : t('app.error')}</Alert>
        </div>
      )}
    </Card>
  );
}

type CapMode = 'default' | 'own' | 'none';

/** An admin's limit on one member's spend per period (PLAN-offers.md B2): the default, their own, or none. */
function CapEditor({ member, defaultCap, currency, onSaved }: { member: Member; defaultCap: string | null; currency: string; onSaved: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  // `none` with a default set means exempt; without a default, inheriting and exempt both mean no limit.
  const initial: CapMode = member.cap_source === 'member' ? 'own' : member.cap_source === 'none' && defaultCap !== null ? 'none' : 'default';
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<CapMode>(initial);
  const [value, setValue] = useState(member.cap_eur ?? '');
  const [error, setError] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(false);
    let choice: MemberCapChoice;
    if (mode === 'default') choice = { use_default: true };
    else if (mode === 'none') choice = { cap_eur: null };
    else {
      const cap = toDecimal(value);
      if (cap === null) return setError(true);
      choice = { cap_eur: cap };
    }
    try {
      await api.setMemberCap(member.user_id, choice);
      setEditing(false);
      await onSaved();
    } catch {
      setError(true);
    }
  };
  if (!editing)
    return (
      <button type="button" className="font-mono text-[12px] text-muted underline decoration-dotted hover:text-ink" onClick={() => setEditing(true)} data-testid={`cap-${member.user_id}`}>
        {member.effective_cap_eur === null
          ? t('org.noCap')
          : member.cap_source === 'default'
            ? t('org.capDefault', { amount: money(member.effective_cap_eur, currency, l) })
            : money(member.effective_cap_eur, currency, l)}
      </button>
    );
  const radio = (m: CapMode, label: string) => (
    <label className="flex items-center gap-1.5 text-[12px] text-ink">
      <input type="radio" name={`cap-mode-${member.user_id}`} className="text-accent focus:ring-0" checked={mode === m} onChange={() => setMode(m)} />
      {label}
    </label>
  );
  return (
    <form onSubmit={save} className="flex min-w-[200px] flex-col gap-1.5">
      {radio('default', defaultCap === null ? t('org.capUseDefaultNone') : t('org.capUseDefault', { amount: money(defaultCap, currency, l) }))}
      <div className="flex items-center gap-1.5">
        {radio('own', t('org.capOwn'))}
        <input
          aria-label={t('org.capAmount', { symbol: currencySymbol(currency, l) })}
          className={`w-20 rounded-sm border px-1.5 py-1 text-[12px] ${error ? 'border-danger' : 'border-line-btn'}`}
          inputMode="decimal"
          value={value}
          onFocus={() => setMode('own')}
          onChange={(e) => {
            setMode('own');
            setValue(e.target.value);
          }}
        />
      </div>
      {radio('none', t('org.capNone'))}
      <div className="flex gap-1">
        <Button type="submit" variant="secondary" className="px-2! py-1! text-[12px]">
          {t('app.save')}
        </Button>
        <Button variant="ghost" className="px-1.5! py-1! text-[12px]" onClick={() => setEditing(false)}>
          {t('app.cancel')}
        </Button>
      </div>
    </form>
  );
}

function InvitationsCard({ org, invitations, onChanged }: { org: Organization; invitations: Invitation[]; onChanged: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const inv = await api.invite(email.trim(), role);
      setNotice({ kind: 'success', text: t('org.invited', { email: inv.email }) });
      setEmail('');
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.is('seats_full')) setNotice({ kind: 'error', text: t('org.seatsFull', { seats: org.subscription?.seats ?? 0 }) });
      else if (err instanceof ApiError && err.is('seats_full_scheduled')) setNotice({ kind: 'error', text: t('org.seatsFullScheduled') });
      else setNotice({ kind: 'error', text: err instanceof ApiError && err.is('already_member') ? t('org.alreadyMember') : t('app.error') });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card
      title={t('org.invitations')}
      actions={
        org.subscription && (
          <span className="text-[12px] text-muted">
            {t('org.seatsLine', { used: org.member_count + invitations.length, seats: org.subscription.seats })} ·{' '}
            <Link to="/billing" className="text-accent underline">
              {t('org.addSeats')}
            </Link>
          </span>
        )
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Field id="invite-email" label={t('org.inviteEmail')} type="email" placeholder={t('auth.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="sm:w-40">
          <Select id="invite-role" label={t('org.inviteRole')} value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'member')}>
            <option value="member">{t('role.member')}</option>
            <option value="admin">{t('role.admin')}</option>
          </Select>
        </div>
        <Button type="submit" disabled={busy || !email.trim()}>
          {busy ? t('org.inviting') : t('org.invite')}
        </Button>
      </form>
      {notice && (
        <div className="mt-3">
          <Alert kind={notice.kind}>{notice.text}</Alert>
        </div>
      )}
      <ul className="mt-4 flex flex-col divide-y divide-line-row">
        {invitations.length === 0 && <li className="py-2 text-[13px] text-faint">{t('org.invitationsNone')}</li>}
        {invitations.map((inv) => (
          <li key={inv.id} className="flex items-center justify-between gap-3 py-2 text-[13px]">
            <div>
              <div className="text-ink">{inv.email}</div>
              <div className="font-mono text-[11px] text-faint">
                {t(`role.${inv.role}`)} · {t('org.expires')} {date(inv.expires_at, i18n.language)}
              </div>
            </div>
            <Button variant="ghost" className="px-2! py-1! text-[12px]" onClick={() => void api.revokeInvitation(inv.id).then(onChanged)}>
              {t('org.revoke')}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ClaimsCard({ claims, onChanged }: { claims: Claim[]; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<ClaimKind>('domain');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.addClaim(kind, value.trim());
      setValue('');
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.is('claim_taken')) setError(t('org.claimTaken'));
      else if (err instanceof ApiError && err.is('claim_refused')) setError(t('org.claimRefused'));
      else setError(t('app.error'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title={t('org.claims')}>
      <p className="text-[13px] text-muted">{t('org.claimsHint')}</p>
      <p className="mt-1 text-[12px] text-faint">{t('org.claimsRules')}</p>
      <ul className="mt-4 flex flex-col divide-y divide-line-row">
        {claims.length === 0 && <li className="py-2 text-[13px] text-faint">{t('org.claimsNone')}</li>}
        {claims.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 py-2 text-[13px]">
            <div>
              <div className="font-mono text-ink">{c.value}</div>
              <div className="text-[11.5px] text-faint">
                {c.kind === 'domain' ? t('org.claimDomain') : t('org.claimTenant')} · {c.verified_at ? <span className="text-accent-dark">{t('org.verified')}</span> : <span className="text-warn">{t('org.pending')}</span>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-[12px] text-muted">
                <input type="checkbox" className="rounded-sm border-line text-accent focus:ring-0" checked={c.auto_join} onChange={(e) => void api.setClaimAutoJoin(c.id, e.target.checked).then(onChanged)} />
                {t('org.autoJoin')}
              </label>
              <Button variant="ghost" className="px-2! py-1! text-[12px]" onClick={() => void api.removeClaim(c.id).then(onChanged)}>
                {t('org.remove')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="sm:w-48">
          <Select id="claim-kind" label={t('org.claimKind')} value={kind} onChange={(e) => setKind(e.target.value as ClaimKind)}>
            <option value="domain">{t('org.claimDomain')}</option>
            <option value="tenant">{t('org.claimTenant')}</option>
          </Select>
        </div>
        <div className="flex-1">
          <Field id="claim-value" label={t('org.claimValue')} placeholder={kind === 'domain' ? t('org.claimDomainPlaceholder') : t('org.claimTenantPlaceholder')} value={value} onChange={(e) => setValue(e.target.value)} required />
        </div>
        <Button type="submit" variant="secondary" disabled={busy || !value.trim()}>
          {busy ? t('org.claiming') : t('org.claim')}
        </Button>
      </form>
      {error && (
        <div className="mt-3">
          <Alert kind="error">{error}</Alert>
        </div>
      )}
    </Card>
  );
}

function LeaveCard({ org, onLeft }: { org: Organization; onLeft: () => Promise<unknown> }) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [left, setLeft] = useState(false);
  const leave = async () => {
    if (!window.confirm(t('org.leaveConfirm', { organization: org.name }))) return;
    setError(null);
    try {
      await api.leaveOrganization();
      setLeft(true);
      await onLeft();
    } catch (err) {
      setError(err instanceof ApiError && err.is('owner_must_transfer') ? t('org.ownerMustTransfer') : t('app.error'));
    }
  };
  if (org.kind === 'personal' && org.member_count <= 1) return null;
  return (
    <Card title={t('org.leave')}>
      <p className="text-[13px] text-muted">{t('org.leaveHint')}</p>
      {error && (
        <div className="mt-3">
          <Alert kind="error">{error}</Alert>
        </div>
      )}
      {left && (
        <div className="mt-3">
          <Alert kind="success">{t('org.left')}</Alert>
        </div>
      )}
      <div className="mt-3">
        <Button variant="danger" onClick={() => void leave()} disabled={left}>
          {t('org.leave')}
        </Button>
      </div>
    </Card>
  );
}
